import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

export default function AirportSearch({ label, name, value, onChange, placeholder = 'City or airport code' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync display when value is set externally (e.g. from URL params)
  useEffect(() => {
    if (value && (!selected || selected.code !== value)) {
      axios.get(`/api/flights/airports?q=${value}`).then(res => {
        const match = res.data.find(a => a.code === value);
        if (match) { setSelected(match); setQuery(`${match.code} – ${match.city}`); }
      }).catch(() => {});
    }
    if (!value) { setSelected(null); setQuery(''); }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = useCallback((q) => {
    if (q.length < 1) { setResults([]); setOpen(false); return; }
    setLoading(true);
    axios.get(`/api/flights/airports?q=${encodeURIComponent(q)}`)
      .then(res => { setResults(res.data); setOpen(true); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleInput = (e) => {
    const q = e.target.value;
    setQuery(q);
    setSelected(null);
    onChange(''); // clear form value while typing
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(q), 200);
  };

  const handleSelect = (airport) => {
    setSelected(airport);
    setQuery(`${airport.code} – ${airport.city}`);
    setOpen(false);
    setResults([]);
    onChange(airport.code);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <div className={`flex items-center border rounded-lg px-3 py-2.5 bg-white transition-all ${
        open ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-300 hover:border-gray-400'
      }`}>
        {/* IATA badge */}
        {selected ? (
          <span className="bg-blue-600 text-white text-xs font-extrabold px-1.5 py-0.5 rounded mr-2 tracking-wide shrink-0">
            {selected.code}
          </span>
        ) : (
          <svg className="w-4 h-4 text-gray-400 mr-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
        )}
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={() => { if (query.length >= 1 && results.length > 0) setOpen(true); }}
          placeholder={placeholder}
          autoComplete="off"
          className="flex-grow text-sm text-gray-800 outline-none bg-transparent placeholder-gray-400"
        />
        {loading && (
          <svg className="w-4 h-4 text-blue-400 animate-spin ml-1 shrink-0" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
        )}
        {selected && (
          <button
            type="button"
            onClick={() => { setSelected(null); setQuery(''); onChange(''); }}
            className="ml-1 text-gray-400 hover:text-red-400 transition-colors shrink-0"
          >
            ✕
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && results.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
          {results.map((airport, i) => (
            <li
              key={`${airport.code}-${i}`}
              onMouseDown={() => handleSelect(airport)}
              className="flex items-start gap-3 px-3 py-2.5 hover:bg-blue-50 cursor-pointer transition-colors"
            >
              <span className="bg-blue-600 text-white text-xs font-extrabold px-1.5 py-0.5 rounded mt-0.5 shrink-0 tracking-wide min-w-[38px] text-center">
                {airport.code}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{airport.city}</p>
                <p className="text-xs text-gray-500 truncate">{airport.name} · {airport.country}</p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {open && !loading && query.length >= 2 && results.length === 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl px-4 py-3 text-sm text-gray-500">
          No airports found for "{query}"
        </div>
      )}

      {/* Hidden input to carry value in form */}
      <input type="hidden" name={name} value={value || ''} />
    </div>
  );
}
